
import { defineComponent, ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";

import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import ExportCustomerModal from "@/components/modals/forms/ExportCustomerModal.vue";
import ApiService from "@/core/services/ApiService";

const NMI_CONNECTION_ID = "1";
const KONNEKTIVE_CONNECTION_ID = "2";
const LIMELIGHT_CONNECTION_ID = "3";
const USAEPAY_CONNECTION_ID = "51";

export default defineComponent({
  name: "merchant-transactions",
  components: {
    Datatable,
    ExportCustomerModal,
  },
  setup() {
    const route = useRoute();
    // eslint-disable-next-line
    const connections = ref<any[]>([]);

    onMounted(async () => {
      setTimeout(async () => {
        try {
          const { data } = await ApiService.get(
            `apis/merchants?merchant_id=${getMerchantId.value}`
          );
          connections.value = [...data.data];
        } catch (e) {
          connections.value = [];
        }

        nmiLoading.value = true;
        nmiTableData.value = await getNmiTransactions(
          nmiPagination.value,
          nmiSort.value
        );
        nmiLoading.value = false;

        usaepayLoading.value = true;
        usaepayTableData.value = await getUsaepayTransactions(
          usaepayPagination.value,
          usaepaySort.value
        );
        usaepayLoading.value = false;

        konnektiveLoading.value = true;
        konnektiveTableData.value = await getKonnektiveTransactions(
          konnektivePagination.value,
          konnektiveSort.value
        );
        konnektiveLoading.value = false;

        limelightLoading.value = true;
        limelightTableData.value = await getLimelightTransactions(
          limelightPagination.value,
          limelightSort.value
        );
        limelightLoading.value = false;
      }, 500);
    });

    const getIsNmiExist = computed(() => {
      const nmi = connections.value.find(
        (conn) => conn.connection_id === NMI_CONNECTION_ID
      );
      if (nmi === undefined) {
        return false;
      }

      return true;
    });

    const getIsKonnektiveExist = computed(() => {
      const konnektive = connections.value.find(
        (conn) => conn.connection_id === KONNEKTIVE_CONNECTION_ID
      );
      if (konnektive === undefined) {
        return false;
      }

      return true;
    });

    const getIsLimelightExist = computed(() => {
      const limelight = connections.value.find(
        (conn) => conn.connection_id === LIMELIGHT_CONNECTION_ID
      );
      if (limelight === undefined) {
        return false;
      }

      return true;
    });
    const getIsUsaEpayExist = computed(() => {
      const usaepay = connections.value.find(
        (conn) => conn.connection_id === USAEPAY_CONNECTION_ID
      );
      if (usaepay === undefined) {
        return false;
      }

      return true;
    });

    const getMerchantId = computed(() => {
      return route.params.id;
    });

    const nmiPagination = ref({
      page: 1,
      rowsPerPage: 5,
    });
    const nmiSort = ref({
      columnName: "",
      order: "",
    });
    const checkedNmiTransactions = ref([]);
    const nmiTableHeader = ref([
      {
        key: "checkbox",
        sortable: false,
      },
      {
        name: "ID",
        key: "id",
        sortable: true,
      },
      {
        name: "Tran ID",
        key: "transaction_id",
        sortable: true,
      },
      {
        name: "Gateway ID",
        key: "gw_processor_id",
        sortable: true,
      },
      {
        name: "CC Bin",
        key: "cc_bin",
        sortable: true,
      },
      {
        name: "CC Last",
        key: "cc_last",
        sortable: true,
      },
      {
        name: "Amount",
        key: "amount",
        sortable: true,
      },
      {
        name: "Type",
        key: "action_type",
        sortable: true,
      },
      {
        name: "Response",
        key: "action_response",
        sortable: true,
      },
      {
        name: "Date",
        key: "date",
        sortable: true,
      },
      {
        name: "Actions",
        key: "actions",
      },
    ]);
    const nmiTableData = ref({
      data: [],
      meta: {
        total: 0,
      },
    });
    const nmiLoading = ref(true);

    const getNmiTransactions = async (pagination, sort) => {
      try {
        const { page, rowsPerPage } = pagination;
        const offset = (page - 1) * rowsPerPage;

        let sortParam = "";
        const { columnName, order } = sort;
        if (columnName !== "") {
          sortParam = `&sort=${columnName},${order}`;
        }
        const { data } = await ApiService.get(
          `transactions/${NMI_CONNECTION_ID}?merchant_id=${getMerchantId.value}&offset=${offset}&limit=${rowsPerPage}${sortParam}`
        );
        return data;
      } catch (err) {
        return {
          data: [],
          meta: {
            total: 0,
          },
        };
      }
    };

    const onNmiCurrentChange = async (val) => {
      nmiPagination.value.page = val;

      nmiLoading.value = true;
      nmiTableData.value = await getNmiTransactions(
        nmiPagination.value,
        nmiSort.value
      );
      nmiLoading.value = false;
    };

    const onNmiSort = async (val) => {
      nmiSort.value = val;

      nmiLoading.value = true;
      nmiTableData.value = await getNmiTransactions(
        nmiPagination.value,
        nmiSort.value
      );
      nmiLoading.value = false;
    };

    const onNmiItemsPerPageChange = async (val) => {
      nmiPagination.value.rowsPerPage = val;

      nmiLoading.value = true;
      nmiTableData.value = await getNmiTransactions(
        nmiPagination.value,
        nmiSort.value
      );
      nmiLoading.value = false;
    };

    const konnektivePagination = ref({
      page: 1,
      rowsPerPage: 5,
    });
    const konnektiveSort = ref({
      columnName: "",
      order: "",
    });
    const checkedKonnektiveTransactions = ref([]);
    const konnektiveTableHeader = ref([
      {
        key: "checkbox",
        sortable: false,
      },
      {
        name: "ID",
        key: "id",
        sortable: true,
      },
      {
        name: "Tran ID",
        key: "transactionId",
        sortable: true,
      },
      {
        name: "MID",
        key: "merchant_id",
        sortable: true,
      },
      {
        name: "Type",
        key: "txnType",
        sortable: true,
      },
      {
        name: "Response",
        key: "responseText",
        sortable: true,
      },
      {
        name: "Mid Tran",
        key: "merchantTxnId",
        sortable: true,
      },
      {
        name: "Total",
        key: "totalAmount",
        sortable: true,
      },
      {
        name: "CC Bin",
        key: "cardBin",
        sortable: true,
      },
      {
        name: "CC Last",
        key: "cardLast4",
        sortable: true,
      },
      {
        name: "Parent Tran",
        key: "parentTxnId",
        sortable: true,
      },
      {
        name: "Actions",
        key: "actions",
      },
    ]);
    const konnektiveTableData = ref({
      data: [],
      meta: {
        total: 0,
      },
    });
    const konnektiveLoading = ref(true);

    const getKonnektiveTransactions = async (pagination, sort) => {
      try {
        const { page, rowsPerPage } = pagination;
        const offset = (page - 1) * rowsPerPage;

        let sortParam = "";
        const { columnName, order } = sort;
        if (columnName !== "") {
          sortParam = `&sort=${columnName},${order}`;
        }
        const { data } = await ApiService.get(
          `transactions/${KONNEKTIVE_CONNECTION_ID}?merchant_id=${getMerchantId.value}&offset=${offset}&limit=${rowsPerPage}${sortParam}`
        );
        return data;
      } catch (err) {
        return {
          data: [],
          meta: {
            total: 0,
          },
        };
      }
    };

    const onKonnektiveCurrentChange = async (val) => {
      konnektivePagination.value.page = val;

      konnektiveLoading.value = true;
      konnektiveTableData.value = await getKonnektiveTransactions(
        konnektivePagination.value,
        konnektiveSort.value
      );
      konnektiveLoading.value = false;
    };

    const onKonnektiveSort = async (val) => {
      konnektiveSort.value = val;

      konnektiveLoading.value = true;
      konnektiveTableData.value = await getKonnektiveTransactions(
        konnektivePagination.value,
        konnektiveSort.value
      );
      konnektiveLoading.value = false;
    };

    const onKonnektiveItemsPerPageChange = async (val) => {
      konnektivePagination.value.rowsPerPage = val;

      konnektiveLoading.value = true;
      konnektiveTableData.value = await getKonnektiveTransactions(
        konnektivePagination.value,
        konnektiveSort.value
      );
      konnektiveLoading.value = false;
    };

    const usaepayPagination = ref({
      page: 1,
      rowsPerPage: 5,
    });
    const usaepaySort = ref({
      columnName: "",
      order: "",
    });
    const checkedUsaepayTransactions = ref([]);
    const usaepayTableHeader = ref([
      {
        key: "checkbox",
        sortable: false,
      },
      {
        name: "ID",
        key: "id",
        sortable: true,
      },
      {
        name: "AccountHolder",
        key: "AccountHolder",
        sortable: true,
      },
      {
        name: "Amount",
        key: "Amount",
        sortable: true,
      },
      {
        name: "AuthCode",
        key: "AuthCode",
        sortable: true,
      },
      {
        name: "CardNumber",
        key: "CardNumber",
        sortable: true,
      },
      {
        name: "CardType",
        key: "CardType",
        sortable: true,
      },
      {
        name: "RefNum",
        key: "RefNum",
        sortable: true,
      },
      {
        name: "Result",
        key: "Result",
        sortable: true,
      },
      {
        name: "Status",
        key: "Status",
        sortable: true,
      },
      {
        name: "Merchant ID",
        key: "merchant_id",
        sortable: true,
      },
      {
        name: "Actions",
        key: "actions",
      },
    ]);
    const usaepayTableData = ref({
      data: [],
      meta: {
        total: 0,
      },
    });
    const usaepayLoading = ref(true);

    const getUsaepayTransactions = async (pagination, sort) => {
      try {
        const { page, rowsPerPage } = pagination;
        const offset = (page - 1) * rowsPerPage;

        let sortParam = "";
        const { columnName, order } = sort;
        if (columnName !== "") {
          sortParam = `&sort=${columnName},${order}`;
        }
        const { data } = await ApiService.get(
          `transactions/${USAEPAY_CONNECTION_ID}?merchant_id=${getMerchantId.value}&offset=${offset}&limit=${rowsPerPage}${sortParam}`
        );
        return data;
      } catch (err) {
        return {
          data: [],
          meta: {
            total: 0,
          },
        };
      }
    };

    const onUsaepayCurrentChange = async (val) => {
      usaepayPagination.value.page = val;

      usaepayLoading.value = true;
      usaepayTableData.value = await getUsaepayTransactions(
        usaepayPagination.value,
        usaepaySort.value
      );
      usaepayLoading.value = false;
    };

    const onUsaepaySort = async (val) => {
      usaepaySort.value = val;

      usaepayLoading.value = true;
      usaepayTableData.value = await getUsaepayTransactions(
        usaepayPagination.value,
        usaepaySort.value
      );
      usaepayLoading.value = false;
    };

    const onUsaepayItemsPerPageChange = async (val) => {
      usaepayPagination.value.rowsPerPage = val;

      usaepayLoading.value = true;
      usaepayTableData.value = await getUsaepayTransactions(
        usaepayPagination.value,
        usaepaySort.value
      );
      usaepayLoading.value = false;
    };

    const limelightPagination = ref({
      page: 1,
      rowsPerPage: 5,
    });
    const limelightSort = ref({
      columnName: "",
      order: "",
    });
    const checkedLimelightTransactions = ref([]);
    const limelightTableHeader = ref([
      {
        key: "checkbox",
        sortable: false,
      },
      {
        name: "ID",
        key: "id",
        sortable: true,
      },
      {
        name: "Order ID",
        key: "order_id",
        sortable: true,
      },
      {
        name: "Gateway ID",
        key: "gateway_id",
        sortable: true,
      },
      {
        name: "CC First 6",
        key: "cc_first_6",
        sortable: true,
      },
      {
        name: "CC Last 4",
        key: "cc_orig_last_4",
        sortable: true,
      },
      {
        name: "Order Total",
        key: "order_total",
        sortable: true,
      },
      {
        name: "Order Status",
        key: "order_status",
        sortable: true,
      },
      {
        name: "Ancestor ID",
        key: "ancestor_id",
        sortable: true,
      },
      {
        name: "Parent ID",
        key: "parent_id",
        sortable: true,
      },
      {
        name: "Actions",
        key: "actions",
      },
    ]);
    const limelightTableData = ref({
      data: [],
      meta: {
        total: 0,
      },
    });
    const limelightLoading = ref(true);

    const getLimelightTransactions = async (pagination, sort) => {
      try {
        const { page, rowsPerPage } = pagination;
        const offset = (page - 1) * rowsPerPage;

        let sortParam = "";
        const { columnName, order } = sort;
        if (columnName !== "") {
          sortParam = `&sort=${columnName},${order}`;
        }
        const { data } = await ApiService.get(
          `transactions/${LIMELIGHT_CONNECTION_ID}?merchant_id=${getMerchantId.value}&offset=${offset}&limit=${rowsPerPage}${sortParam}`
        );
        return data;
      } catch (err) {
        return {
          data: [],
          meta: {
            total: 0,
          },
        };
      }
    };

    const onLimelightCurrentChange = async (val) => {
      limelightPagination.value.page = val;

      limelightLoading.value = true;
      limelightTableData.value = await getLimelightTransactions(
        limelightPagination.value,
        limelightSort.value
      );
      limelightLoading.value = false;
    };

    const onLimelightSort = async (val) => {
      limelightSort.value = val;

      limelightLoading.value = true;
      limelightTableData.value = await getLimelightTransactions(
        limelightPagination.value,
        limelightSort.value
      );
      limelightLoading.value = false;
    };

    const onLimelightItemsPerPageChange = async (val) => {
      limelightPagination.value.rowsPerPage = val;

      limelightLoading.value = true;
      limelightTableData.value = await getLimelightTransactions(
        limelightPagination.value,
        limelightSort.value
      );
      limelightLoading.value = false;
    };

    const selectedConn = ref(NMI_CONNECTION_ID);

    const getIsDataEmpty = computed(() => {
      if (getIsNmiExist.value && nmiTableData.value.meta.total !== 0) {
        return false;
      } else if (
        getIsKonnektiveExist.value &&
        konnektiveTableData.value.meta.total !== 0
      ) {
        return false;
      } else if (
        getIsLimelightExist.value &&
        limelightTableData.value.meta.total !== 0
      ) {
        return false;
      } else if (
        getIsUsaEpayExist.value &&
        usaepayTableData.value.meta.total !== 0
      ) {
        return false;
      } else {
        return true;
      }
    });

    const onChangeConn = (val) => {
      selectedConn.value = val;
    };

    return {
      nmiTableData,
      nmiTableHeader,
      nmiLoading,
      checkedNmiTransactions,
      nmiPagination,

      onNmiCurrentChange,
      onNmiSort,
      onNmiItemsPerPageChange,

      konnektiveTableData,
      konnektiveTableHeader,
      konnektiveLoading,
      checkedKonnektiveTransactions,
      konnektivePagination,

      onKonnektiveCurrentChange,
      onKonnektiveSort,
      onKonnektiveItemsPerPageChange,

      usaepayTableData,
      usaepayTableHeader,
      usaepayLoading,
      checkedUsaepayTransactions,
      usaepayPagination,

      onUsaepayCurrentChange,
      onUsaepaySort,
      onUsaepayItemsPerPageChange,

      limelightTableData,
      limelightTableHeader,
      limelightLoading,
      checkedLimelightTransactions,
      limelightPagination,

      onLimelightCurrentChange,
      onLimelightSort,
      onLimelightItemsPerPageChange,

      getIsDataEmpty,
      onChangeConn,

      NMI_CONNECTION_ID,
      KONNEKTIVE_CONNECTION_ID,
      LIMELIGHT_CONNECTION_ID,
      USAEPAY_CONNECTION_ID,

      getIsNmiExist,
      getIsKonnektiveExist,
      getIsLimelightExist,
      getIsUsaEpayExist,
    };
  },
});
